import {
  CheckIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import React, {useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import Community from '../../domain/community'
import {useAssignSponsor} from '../../hooks/useAssignSponsor'
import {useCommunitySponsorAssignments} from '../../hooks/useCommunitySponsorAssignments'
import {Sponsor} from '../../hooks/useSponsors'
import {getCommunitiesAction} from '../../store/communities/communities.action'
import {getAllCommunities} from '../../store/communities/communities.selectors'
import {Community as CommunityT} from '../../store/communities/communities.type'
import {AppDispatch, AppState} from '../../store/store'
import {SponsorImageKey} from '../../types/lessonKeys'
import {
  LessonSponsorAssignment,
  SponsorInfo
} from '../../types/sponsorAssignments'
import Dialog from '../Dialog'
import FlatButton from '../FlatButton'
import ImageUpload from '../SponsorsPage/components/ImageUpload'
import SponsorAssignmentTable from './SponsorAssignmentTable'

type Props = Readonly<{
  sponsor: Sponsor | null
  sponsors: Sponsor[]
  open: boolean
  onClose: () => void
  onCommunitySelected?: (community: CommunityT) => void
  onImageUpload?: (sponsor: Sponsor, image: File) => void
  onImageDelete?: (sponsor: Sponsor) => void
}>

function CommunityListItem({
  community,
  onClick
}: {
  community: CommunityT
  onClick: (community: CommunityT) => void
}) {
  const formattedName = Community.formatName(community)

  return (
    <button
      type="button"
      className="w-full border-b border-grey-tertiary-10 px-4 py-3 text-left last:border-b-0 hover:bg-grey-tertiary-10 focus:bg-grey-tertiary-10 focus:outline-none"
      onClick={() => onClick(community)}
    >
      <div className="flex items-center justify-between">
        <span className="typography-body-1 text-navy">{formattedName}</span>
        <svg
          className="size-5 text-grey-tertiary-20"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </button>
  )
}

function AssignCommunitySponsorDialog({
  sponsor,
  open,
  onClose,
  onCommunitySelected,
  onImageUpload,
  onImageDelete,
  sponsors
}: Props) {
  const intl = useIntl()
  const dispatch = useDispatch<AppDispatch>()
  const communities = useSelector(getAllCommunities)
  const state = useSelector((state: AppState) => state)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [loading, setLoading] = useState(false)

  // Two-step flow state
  const [selectedCommunity, setSelectedCommunity] = useState<CommunityT | null>(
    null
  )
  const [pendingAssignments, setPendingAssignments] = useState<
    LessonSponsorAssignment[]
  >([])

  // Use the custom hook for fetching assignments
  const {
    assignments: currentAssignments,
    isLoading: loadingAssignments,
    error: assignmentsError,
    fetchAssignments,
    clearAssignments,
    updateAssignments
  } = useCommunitySponsorAssignments({
    onError: error => {
      console.error('Failed to load sponsor assignments:', error)
    },
    onSuccess: assignments => {
      setPendingAssignments([...assignments]) // Create a copy for local changes
    }
  })

  // Use the assign sponsor hook for saving assignments
  const {assignSponsor, isAssigning: isSavingAssignments} = useAssignSponsor({
    onError: error => {
      console.error('Failed to save sponsor assignments:', error)
    },
    onSuccess: () => {
      updateAssignments([...pendingAssignments])

      // Call the original callback if provided
      if (onCommunitySelected && selectedCommunity) {
        onCommunitySelected(selectedCommunity)
      }

      onClose()
    }
  })

  // Convert sponsors from hook to SponsorInfo format
  const allSponsors: SponsorInfo[] =
    sponsors?.map(sponsor => ({
      id: sponsor.id,
      name: sponsor.name
    })) || []

  const filteredCommunities = communities.filter(community =>
    Community.formatName(community)
      .toLocaleLowerCase()
      .includes(searchTerm.toLocaleLowerCase())
  )

  useEffect(() => {
    if (open && communities.length === 0) {
      setLoading(true)
      dispatch(getCommunitiesAction())
        .catch(error => {
          console.error('Failed to load communities:', error)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [open, communities.length, dispatch])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedCommunity(null)
      clearAssignments()
      setPendingAssignments([])
      setSearchTerm('')
    }
  }, [open, clearAssignments])

  const handleCommunityClick = (community: CommunityT) => {
    setSelectedCommunity(community)
    fetchAssignments(community.id, state)
  }

  const handleBackToCommunitySelection = () => {
    setSelectedCommunity(null)
    clearAssignments()
    setPendingAssignments([])
  }

  const handleAssignmentChange = (
    lessonKey: SponsorImageKey,
    sponsorId: string | null
  ) => {
    setPendingAssignments(prev =>
      prev.map(assignment =>
        assignment.lessonKey === lessonKey
          ? {...assignment, sponsorId}
          : assignment
      )
    )
  }

  const handleModuleChange = (
    moduleKey: SponsorImageKey,
    lessonType: '_START' | '_COMPLETION',
    isChecked: boolean
  ) => {
    if (!sponsor) return

    const moduleNumber = moduleKey.replace('MODULE_', '')
    const newSponsorId = isChecked ? sponsor.id : null

    setPendingAssignments(prev =>
      prev.map(assignment => {
        // Only update lessons in this module that are available (not assigned to other sponsors)
        if (
          assignment.lessonKey.includes(`LESSON_${moduleNumber}_`) &&
          assignment.lessonKey.includes(lessonType) &&
          (!assignment.sponsorId || assignment.sponsorId === sponsor.id)
        ) {
          return {...assignment, sponsorId: newSponsorId}
        }
        return assignment
      })
    )
  }

  const handleSave = () => {
    if (!sponsor || !selectedCommunity) return

    assignSponsor({
      type: 'community',
      sponsorId: sponsor.id,
      communityId: selectedCommunity.id,
      assignments: pendingAssignments,
      state
    })
  }

  const handleCancel = () => {
    if (selectedCommunity) {
      // Reset pending changes to current assignments
      setPendingAssignments([...currentAssignments])
      onClose()
    } else {
      onClose()
    }
  }

  const hasUnsavedChanges =
    JSON.stringify(currentAssignments) !== JSON.stringify(pendingAssignments)

  const handleAssignAll = () => {
    if (!sponsor) return

    setPendingAssignments(prev =>
      prev.map(assignment => {
        // Only assign if not already assigned to a different sponsor
        const isAvailable =
          !assignment.sponsorId || assignment.sponsorId === sponsor.id

        return {
          ...assignment,
          sponsorId: isAvailable ? sponsor.id : assignment.sponsorId
        }
      })
    )
  }

  const handleUnassignAll = () => {
    if (!sponsor) return

    setPendingAssignments(prev =>
      prev.map(assignment => ({
        ...assignment,
        sponsorId:
          assignment.sponsorId === sponsor.id ? null : assignment.sponsorId
      }))
    )
  }

  const handleImageUpload = (_: string, image: File) => {
    if (sponsor && onImageUpload) {
      onImageUpload(sponsor, image)
    }
  }

  const handleImageDelete = () => {
    if (sponsor && onImageDelete) {
      onImageDelete(sponsor)
    }
  }

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth>
      <div className="mx-auto flex w-full max-w-4xl flex-col">
        <div className="-mt-6 mb-6 flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            {sponsor && (
              <ImageUpload
                uploadKey={sponsor.id}
                uri={sponsor.imageUrl || null}
                onUpload={handleImageUpload}
                onDelete={handleImageDelete}
                size="small"
              />
            )}
            <Dialog.Title className="typography-headline-4">
              <FormattedMessage id="admin.sponsors.assignCommunity.title" />
            </Dialog.Title>
          </div>
          <button className="-mr-2 -mt-2" type="button" onClick={handleCancel}>
            <span className="sr-only">
              <FormattedMessage id="general.Close" />
            </span>
            <XMarkIcon aria-hidden className="size-7" />
          </button>
        </div>

        {/* Step 1: Community Selection */}
        {!selectedCommunity && (
          <>
            {/* Search Input */}
            <div className="mb-4">
              <div className="relative rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <MagnifyingGlassIcon
                    className="size-5 text-grey-tertiary-20"
                    aria-hidden="true"
                  />
                </div>
                <input
                  onChange={({target: {value: newSearchTerm}}) =>
                    setSearchTerm(newSearchTerm)
                  }
                  type="text"
                  name="search"
                  id="community-search"
                  value={searchTerm || undefined}
                  className="sm:text-sm block w-full rounded-md border-0 py-1.5 pl-10 text-navy outline-none ring-1 ring-inset ring-grey-tertiary-10 placeholder:text-grey-tertiary-20 focus:outline-none focus:ring-inset focus-visible:border-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-grey-tertiary-10 focus-visible:ring-offset-1 focus-visible:ring-offset-grey-tertiary-10 sm:leading-6"
                  placeholder={intl.formatMessage({
                    id: 'admin.communities.search'
                  })}
                />
              </div>
            </div>

            {/* Communities List */}
            <div className="max-h-[32rem] overflow-y-auto rounded-md border border-grey-tertiary-10">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-pulse">
                    <FormattedMessage id="learners.loading" />
                  </div>
                </div>
              ) : filteredCommunities.length === 0 ? (
                <div className="p-8 text-center text-grey-tertiary-20">
                  {searchTerm ? (
                    <FormattedMessage id="admin.communities.notFoundError" />
                  ) : (
                    <FormattedMessage id="admin.communities.selectSearch" />
                  )}
                </div>
              ) : (
                filteredCommunities.map(community => (
                  <CommunityListItem
                    key={community.id}
                    community={community}
                    onClick={handleCommunityClick}
                  />
                ))
              )}
            </div>
          </>
        )}

        {/* Step 2: Sponsor Assignment */}
        {selectedCommunity && (
          <>
            {/* Back button, Community display, and Assign/Unassign buttons */}
            <div className="mb-4 flex items-center gap-4">
              <FlatButton
                variant="secondary"
                onClick={handleBackToCommunitySelection}
              >
                ← Back
              </FlatButton>

              <div className="w-48 shrink-0">
                <div className="truncate rounded-md border px-3 py-2">
                  {Community.formatName(selectedCommunity)}
                </div>
              </div>

              <div className="ml-auto flex gap-2">
                <FlatButton
                  variant="secondary"
                  onClick={handleUnassignAll}
                  icon={
                    <XMarkIcon className="size-4 rounded-sm border border-black" />
                  }
                >
                  Unassign all
                </FlatButton>
                <FlatButton
                  variant="success"
                  onClick={handleAssignAll}
                  icon={
                    <CheckIcon className="size-4 rounded-sm border border-black" />
                  }
                >
                  Assign all
                </FlatButton>
              </div>
            </div>

            {/* Sponsor Assignment Table */}
            {sponsor && (
              <SponsorAssignmentTable
                assignments={pendingAssignments}
                currentSponsor={{id: sponsor.id, name: sponsor.name}}
                allSponsors={allSponsors}
                isLoading={loadingAssignments}
                error={assignmentsError}
                onChange={handleAssignmentChange}
                onModuleChange={handleModuleChange}
              />
            )}

            {/* Save/Cancel Buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <FlatButton
                variant="secondary"
                onClick={handleCancel}
                disabled={isSavingAssignments}
              >
                <FormattedMessage id="admin.manageSponsors.cancel" />
              </FlatButton>
              <FlatButton
                variant="success"
                onClick={handleSave}
                disabled={isSavingAssignments || !hasUnsavedChanges}
              >
                Save
              </FlatButton>
            </div>
          </>
        )}
      </div>
    </Dialog>
  )
}

export default AssignCommunitySponsorDialog
